import { ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  FilePlus,
  PackageOpen,
  Settings,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  LayoutDashboard,
  SprayCan,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import ToastContainer from './Toast';
import NotificationBell from './NotificationBell';
import AboutModal, { type AboutInfo } from './AboutModal';
import {
  APP_NAME,
  NAV_LABELS,
  ROLE_LABELS,
  SUPPORTED_FORMATS,
  UNSUPPORTED_FORMATS,
  RC_LABEL,
} from '../config/labels';

interface NavItem {
  to: string;
  label: string;
  icon: typeof FolderOpen;
  roles?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: NAV_LABELS.dashboard, icon: LayoutDashboard },
  { to: '/cases', label: NAV_LABELS.cases, icon: FolderOpen },
  { to: '/cases/new', label: NAV_LABELS.newCase, icon: FilePlus },
  { to: '/cases/package-drop', label: NAV_LABELS.packageDrop, icon: PackageOpen },
  { to: '/tools/data-wash', label: NAV_LABELS.dataWash, icon: SprayCan },
];

// Settings (formerly Admin) lives at the BOTTOM of the sidebar menu,
// below the main nav, alongside the RC version label (operator 2026-09-21).
const SETTINGS_ITEM: NavItem = {
  to: '/admin',
  label: NAV_LABELS.admin,
  icon: Settings,
  roles: ['operator', 'compliance'],
};

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Env indicator (dev/uat/prod) fetched from the backend /health `profile`.
  const [envProfile, setEnvProfile] = useState<string>('');
  // rc_version + build timestamp for the About popup.
  const [aboutInfo, setAboutInfo] = useState<AboutInfo | null>(null);
  const [showAbout, setShowAbout] = useState(false);
  useEffect(() => {
    const base = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || '/api/v1';
    fetch(`${base}/health`)
      .then((r) => r.json())
      .then((d: { profile?: string; rc_version?: string; version?: string }) => {
        if (d.profile) setEnvProfile(d.profile);
        setAboutInfo({
          profile: d.profile || 'dev',
          rc_version: d.rc_version || 'dev',
          version: d.version || 'dev',
        });
      })
      .catch(() => { /* best-effort; version still shows */ });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 flex-shrink-0 z-30">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle navigation"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-primary-600" size={24} />
              <span className="font-semibold text-slate-900 text-base hidden sm:inline">
                {APP_NAME}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-800">{user?.full_name}</div>
              <div className="text-xs text-slate-500">
                {user ? ROLE_LABELS[user.role] : ''}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              aria-label="Logout"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-20 w-60 bg-white border-r border-slate-200 transition-transform duration-200 flex flex-col`}
        >
          <nav className="flex-1 p-3 space-y-1 mt-14 lg:mt-0 overflow-y-auto">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'nav-link-active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          {/* Bottom of the menu: Settings (renamed from Admin) + RC version */}
          <div className="p-3 border-t border-slate-200">
            {(!SETTINGS_ITEM.roles || (user && SETTINGS_ITEM.roles.includes(user.role))) && (
              <NavLink
                to={SETTINGS_ITEM.to}
                end
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'nav-link-active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <SETTINGS_ITEM.icon size={18} />
                <span>{SETTINGS_ITEM.label}</span>
              </NavLink>
            )}
            <div className="text-[11px] text-slate-400 mt-2 px-2 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowAbout(true)}
                className="hover:text-primary-600 transition-colors cursor-pointer"
                title="About this build"
              >
                {RC_LABEL}
              </button>
              {envProfile && <span className="text-slate-500 font-medium">· {envProfile}</span>}
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/30 z-10"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 animate-fade-in">
          {children}
          {/* Footer (scrolls with content; RC version also in sidebar) */}
          <footer className="bg-white border-t border-slate-200 px-4 py-3 mt-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-xs text-slate-500">
                <span className="font-medium text-slate-600">Supported formats:</span>{' '}
                {SUPPORTED_FORMATS.join(', ')}
                <span className="ml-2 text-slate-400">
                  (unsupported: {UNSUPPORTED_FORMATS.join(', ')})
                </span>
              </div>
            </div>
          </footer>
        </main>
      </div>

      <ToastContainer />
      {showAbout && (
        <AboutModal info={aboutInfo} onClose={() => setShowAbout(false)} />
      )}
    </div>
  );
}